/**
 * GameConfigForm.js
 * Dynamic form that renders based on game type's configuration schema
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    TextField,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    Typography,
    Paper,
    Grid,
    Slider
} from '@mui/material';
import gameManagementApi from './gameManagementApi';
import SpacesEditor from './SpacesEditor';

export default function GameConfigForm({ gameTypeDetails, config, gameName, onChange }) {
    const [localConfig, setLocalConfig] = useState(config);
    const [localGameName, setLocalGameName] = useState(gameName);
    const [isLoadingLatestConfig, setIsLoadingLatestConfig] = useState(false);
    const debounceTimer = useRef(null);
    const hasLoadedLatestConfig = useRef(false);

    // Load latest game config on mount
    useEffect(() => {
        const loadLatestConfig = async () => {
            if (hasLoadedLatestConfig.current) return;

            try {
                setIsLoadingLatestConfig(true);
                const result = await gameManagementApi.getLatestGameConfig();

                if (result.success && result.config && result.gameType === gameTypeDetails.id) {
                    // Merge latest config with current config
                    const mergedConfig = { ...config, ...result.config };
                    setLocalConfig(mergedConfig);
                    onChange(mergedConfig, localGameName);
                    hasLoadedLatestConfig.current = true;
                }
            } catch (error) {
                console.error('Failed to load latest game config:', error);
                // Silently fail - just use default config
            } finally {
                setIsLoadingLatestConfig(false);
            }
        };

        loadLatestConfig();
    }, [gameTypeDetails.id]); // Only run when game type changes

    useEffect(() => {
        setLocalConfig(config);
        setLocalGameName(gameName);
    }, [config, gameName]);

    // Debounced update to parent - only after user stops typing for 300ms
    const debouncedOnChange = useCallback((newConfig, newName) => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            onChange(newConfig, newName);
        }, 300);
    }, [onChange]);

    const handleConfigChange = (key, value) => {
        const newConfig = { ...localConfig, [key]: value };
        setLocalConfig(newConfig);
        debouncedOnChange(newConfig, localGameName);
    };

    const handleGameNameChange = (value) => {
        setLocalGameName(value);
        debouncedOnChange(localConfig, value);
    };

    const handlePresetScenarioChange = async (scenarioId) => {
        if (scenarioId === 'custom') {
            handleConfigChange('preset_scenario', scenarioId);
            return;
        }

        try {
            const result = await gameManagementApi.getPresetScenario(gameTypeDetails.id, scenarioId);
            if (result.success) {
                const scenario = result.scenario;
                const newConfig = {
                    ...localConfig,
                    preset_scenario: scenarioId,
                    objective: scenario.objective,
                    scene_description: scenario.scene_description,
                    max_rounds: scenario.max_rounds
                };
                setLocalConfig(newConfig);
                onChange(newConfig, localGameName);

                // Also update players if available (will be handled in PlayerConfiguration)
                if (scenario.players && window.updatePlayersFromScenario) {
                    window.updatePlayersFromScenario(scenario.players);
                }
            }
        } catch (error) {
            console.error('Failed to load preset scenario:', error);
        }
    };

    const renderField = (key, fieldSchema) => {
        const value = localConfig[key];

        switch (fieldSchema.type) {
            case 'number':
                return (
                    <Box key={key} sx={{ mb: 3 }}>
                        <Typography gutterBottom>
                            {fieldSchema.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {fieldSchema.description}
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs>
                                <Slider
                                    value={value || fieldSchema.default}
                                    onChange={(e, newValue) => handleConfigChange(key, newValue)}
                                    min={fieldSchema.min}
                                    max={fieldSchema.max}
                                    marks
                                    valueLabelDisplay="auto"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    type="number"
                                    value={value || fieldSchema.default}
                                    onChange={(e) => handleConfigChange(key, parseInt(e.target.value))}
                                    inputProps={{
                                        min: fieldSchema.min,
                                        max: fieldSchema.max
                                    }}
                                    sx={{ width: 80 }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 'boolean':
                return (
                    <Box key={key} sx={{ mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={value || fieldSchema.default || false}
                                    onChange={(e) => handleConfigChange(key, e.target.checked)}
                                />
                            }
                            label={
                                <Box>
                                    <Typography>{fieldSchema.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {fieldSchema.description}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                );

            case 'select':
                return (
                    <FormControl key={key} fullWidth sx={{ mb: 3 }}>
                        <InputLabel>{fieldSchema.label}</InputLabel>
                        <Select
                            value={value || fieldSchema.default}
                            label={fieldSchema.label}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                if (key === 'preset_scenario') {
                                    handlePresetScenarioChange(newValue);
                                } else {
                                    handleConfigChange(key, newValue);
                                }
                            }}
                        >
                            {fieldSchema.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {fieldSchema.description}
                        </Typography>
                    </FormControl>
                );

            case 'text':
            case 'string':
                return (
                    <TextField
                        key={key}
                        fullWidth
                        label={fieldSchema.label}
                        value={value || fieldSchema.default || ''}
                        onChange={(e) => handleConfigChange(key, e.target.value)}
                        helperText={fieldSchema.description}
                        required={fieldSchema.required}
                        sx={{ mb: 3 }}
                    />
                );

            case 'textarea':
                return (
                    <TextField
                        key={key}
                        fullWidth
                        multiline
                        rows={4}
                        label={fieldSchema.label}
                        value={value || fieldSchema.default || ''}
                        onChange={(e) => handleConfigChange(key, e.target.value)}
                        helperText={fieldSchema.description}
                        required={fieldSchema.required}
                        sx={{ mb: 3 }}
                    />
                );

            case 'json':
                return (
                    <TextField
                        key={key}
                        fullWidth
                        multiline
                        rows={6}
                        label={fieldSchema.label}
                        value={value ? JSON.stringify(value, null, 2) : ''}
                        onChange={(e) => {
                            try {
                                const parsed = JSON.parse(e.target.value);
                                handleConfigChange(key, parsed);
                            } catch (err) {
                                // Invalid JSON, don't update
                            }
                        }}
                        helperText={fieldSchema.description}
                        required={fieldSchema.required}
                        sx={{ mb: 3 }}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Configure {gameTypeDetails.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Set up the game parameters and rules.
            </Typography>

            <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
                <TextField
                    fullWidth
                    label="Game Name"
                    value={localGameName}
                    onChange={(e) => handleGameNameChange(e.target.value)}
                    helperText="Give your game a unique name"
                    required
                    sx={{ mb: 3 }}
                />

                {Object.entries(gameTypeDetails.configSchema).map(([key, fieldSchema]) =>
                    renderField(key, fieldSchema)
                )}

                {/* Spaces Configuration */}
                <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    <SpacesEditor
                        spaces={localConfig.spaces}
                        onChange={(newSpaces) => handleConfigChange('spaces', newSpaces)}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
